import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, LayoutGrid, List, Pencil, Trash2, Check, EyeOff, Eye, CalendarDays,
  GripVertical, FileText, X, StickyNote, ListChecks, Sparkles,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

const PRIORITY_META = {
  low: { label: "Low", chip: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200", dot: "#94a3b8" },
  medium: { label: "Medium", chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200", dot: "#0ea5e9" },
  high: { label: "High", chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200", dot: "#f59e0b" },
  urgent: { label: "Urgent", chip: "bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-200", dot: "#dc2626" },
};

function dueMeta(due) {
  if (!due) return null;
  const d = parseISO(due);
  const days = Math.ceil((d - new Date().setHours(0, 0, 0, 0)) / 86400000);
  const label = format(d, "MMM d");
  if (days < 0) return { label, tone: "text-red-600 dark:text-red-400", note: `${-days}d overdue` };
  if (days === 0) return { label, tone: "text-amber-600 dark:text-amber-400", note: "Today" };
  if (days <= 3) return { label, tone: "text-amber-600 dark:text-amber-400", note: `in ${days}d` };
  return { label, tone: "text-muted-foreground", note: "" };
}

function PriorityChip({ p }) {
  const m = PRIORITY_META[p] || PRIORITY_META.medium;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.chip}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: m.dot }} />{m.label}
    </span>
  );
}

function AssigneeStack({ assignees = [], max = 3 }) {
  if (!assignees.length) return <span className="text-[11px] text-muted-foreground/60">Unassigned</span>;
  const shown = assignees.slice(0, max);
  const extra = assignees.length - shown.length;
  return (
    <div className="flex items-center -space-x-2">
      {shown.map((a) => <Avatar key={a.user_id} name={a.name} src={a.avatar_url} size="sm" single title={a.name} />)}
      {extra > 0 && <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-[11px] font-bold text-muted-foreground ring-2 ring-background">+{extra}</span>}
    </div>
  );
}

function DuePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid="task-due-picker"
          className={`w-full justify-start gap-2 font-normal ${!value ? "text-muted-foreground" : ""}`}>
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          {value ? format(selected, "MMM d, yyyy") : "No due date"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={selected} defaultMonth={selected}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : null); setOpen(false); }} />
        {value && <div className="border-t p-2"><Button variant="ghost" size="sm" className="w-full" onClick={() => { onChange(null); setOpen(false); }}>Clear</Button></div>}
      </PopoverContent>
    </Popover>
  );
}

export default function Tasks() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [view, setView] = useState("kanban");
  const [showHidden, setShowHidden] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tmplOpen, setTmplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [newTmpl, setNewTmpl] = useState(null);
  const dragId = useRef(null);
  const [dragCol, setDragCol] = useState(null);
  const [aiPrompt, setAiPrompt] = useState(null); // {text} | null
  const [aiLoading, setAiLoading] = useState(false);

  const canManage = !!data?.can_manage;

  const load = (hidden = showHidden) =>
    api.get(`/tasks${hidden ? "?include_hidden=true" : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) navigate("/dashboard"); });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("ai") === "1") setAiPrompt({ text: "" });
  }, []);
  useEffect(() => { load(showHidden); /* eslint-disable-next-line */ }, [showHidden]);

  const loadTemplates = () => api.get("/task-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));

  const columns = data?.columns || [];
  const cards = data?.cards || [];
  const assignees = data?.assignees || [];
  const byCol = (key) => cards.filter((c) => c.column === key);

  const saveTask = async () => {
    if (!editing?.title?.trim()) { toast.error("Title is required"); return; }
    const body = {
      title: editing.title.trim(), description: editing.description || "",
      column: editing.column, priority: editing.priority || "medium",
      assignee_ids: editing.assignee_ids || [], due_date: editing.due_date || null,
      notes: editing.notes || "", checklist: (editing.checklist || []).filter((i) => (i.text || "").trim()),
    };
    try {
      if (editing.task_id) await api.put(`/tasks/${editing.task_id}`, body);
      else await api.post("/tasks", body);
      toast.success(editing.task_id ? "Task updated" : "Task created");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
  };

  const act = async (id, path, ok) => {
    try { await api.post(`/tasks/${id}/${path}`); toast.success(ok); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
  };

  const del = async (id) => {
    try { await api.delete(`/tasks/${id}`); toast.success("Task deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const onDrop = async (colKey) => {
    const id = dragId.current; dragId.current = null; setDragCol(null);
    if (!id) return;
    const card = cards.find((c) => c.task_id === id);
    if (!card || card.column === colKey) return;
    setData((d) => ({ ...d, cards: d.cards.map((c) => c.task_id === id ? { ...c, column: colKey } : c) }));
    try { await api.post(`/tasks/${id}/move`, { column: colKey }); }
    catch { toast.error("Failed to move"); load(); }
  };

  const openTemplates = () => { loadTemplates(); setTmplOpen(true); };

  const applyTemplate = (t) => {
    setTmplOpen(false);
    setEditing({
      title: t.title, description: t.description || "",
      column: t.column || columns[0]?.key, priority: t.priority || "medium",
      assignee_ids: [], due_date: null, notes: "", checklist: [],
    });
  };

  const saveTemplate = async () => {
    if (!newTmpl?.title?.trim()) { toast.error("Title is required"); return; }
    try {
      await api.post("/task-templates", {
        title: newTmpl.title.trim(), description: newTmpl.description || "",
        column: newTmpl.column || null, priority: newTmpl.priority || "medium",
      });
      toast.success("Template saved"); setNewTmpl(null); loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const delTemplate = async (id) => {
    try { await api.delete(`/task-templates/${id}`); loadTemplates(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  // Edit-dialog helpers for assignees + checklist
  const toggleAssignee = (uid) => setEditing((p) => {
    const cur = p.assignee_ids || [];
    return { ...p, assignee_ids: cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid] };
  });
  const addCheckItem = () => setEditing((p) => ({ ...p, checklist: [...(p.checklist || []), { id: Math.random().toString(36).slice(2, 10), text: "", done: false }] }));
  const updCheckItem = (id, patch) => setEditing((p) => ({ ...p, checklist: (p.checklist || []).map((i) => i.id === id ? { ...i, ...patch } : i) }));
  const delCheckItem = (id) => setEditing((p) => ({ ...p, checklist: (p.checklist || []).filter((i) => i.id !== id) }));

  const runAiDraft = async () => {
    const text = (aiPrompt?.text || "").trim();
    if (!text) { toast.error("Describe the incident first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/assistant/draft-task", { description: text, column: "incident" });
      const d = data.draft;
      setAiPrompt(null);
      setEditing({
        title: d.title, description: d.description || "", column: d.column || "incident",
        priority: d.priority || "medium", assignee_ids: [], due_date: null, notes: "",
        checklist: (d.checklist || []).map((c) => ({ id: Math.random().toString(36).slice(2, 10), text: c.text, done: !!c.done })),
      });
      toast.success("Draft ready — review and create");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not draft a task");
    } finally { setAiLoading(false); }
  };

  const CardMenu = ({ c }) => {
    const done = c.status === "completed";
    const hidden = c.status === "hidden";
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`task-menu-${c.task_id}`} className="rounded p-1 text-muted-foreground hover:bg-muted"><GripVertical className="h-4 w-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid={`task-edit-${c.task_id}`} onClick={() => setEditing({ ...c })}><Pencil className="mr-2 h-3.5 w-3.5" /> Edit</DropdownMenuItem>
          {canManage && <>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={`task-complete-${c.task_id}`} onClick={() => act(c.task_id, "complete", done ? "Reopened" : "Completed")}><Check className="mr-2 h-3.5 w-3.5" /> {done ? "Reopen" : "Complete"}</DropdownMenuItem>
            <DropdownMenuItem data-testid={`task-hide-${c.task_id}`} onClick={() => act(c.task_id, "hide", hidden ? "Unhidden" : "Hidden")}>{hidden ? <><Eye className="mr-2 h-3.5 w-3.5" /> Unhide</> : <><EyeOff className="mr-2 h-3.5 w-3.5" /> Hide</>}</DropdownMenuItem>
            <DropdownMenuItem data-testid={`task-delete-${c.task_id}`} className="text-red-600" onClick={() => del(c.task_id)}><Trash2 className="mr-2 h-3.5 w-3.5" /> Delete</DropdownMenuItem>
          </>}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const Card = ({ c }) => {
    const due = dueMeta(c.due_date);
    const done = c.status === "completed";
    const hidden = c.status === "hidden";
    return (
      <div
        data-testid={`task-card-${c.task_id}`}
        draggable
        onDragStart={() => { dragId.current = c.task_id; }}
        onDragEnd={() => { dragId.current = null; setDragCol(null); }}
        className={`group cursor-grab rounded-xl border bg-card p-3 shadow-sm transition-all hover:shadow-md active:cursor-grabbing ${done ? "border-green-300 bg-green-50/50 dark:bg-green-950/30" : hidden ? "border-dashed border-border opacity-60" : "border-border"}`}
        onDoubleClick={() => setEditing({ ...c })}
      >
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-semibold leading-snug text-foreground ${done ? "line-through opacity-60" : ""}`}>{c.title}</p>
          <CardMenu c={c} />
        </div>
        {c.description && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{c.description}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          {c.checklist_total > 0 && <span className={`inline-flex items-center gap-1 font-medium ${c.checklist_done === c.checklist_total ? "text-green-600 dark:text-green-400" : ""}`}><ListChecks className="h-3.5 w-3.5" />{c.checklist_done}/{c.checklist_total}</span>}
          {c.notes && <StickyNote className="h-3.5 w-3.5" title="Has notes" />}
        </div>
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <PriorityChip p={c.priority} />
            {due && <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${due.tone}`}><CalendarDays className="h-3 w-3" />{due.label}{due.note && <span className="opacity-70">· {due.note}</span>}</span>}
          </div>
          <AssigneeStack assignees={c.assignees} />
        </div>
        {done && c.completed_by_name && <p className="mt-2 text-[11px] text-green-700 dark:text-green-400">Completed by {c.completed_by_name}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Task Management</h1>
          <p className="mt-1 text-sm text-muted-foreground">Engineering board — incidents, maintenance and team priorities.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border border-border bg-card p-0.5">
            <button data-testid="tasks-view-kanban" onClick={() => setView("kanban")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "kanban" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><LayoutGrid className="h-4 w-4" /> Board</button>
            <button data-testid="tasks-view-list" onClick={() => setView("list")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}><List className="h-4 w-4" /> List</button>
          </div>
          {canManage && (
            <Button variant="outline" size="sm" data-testid="tasks-toggle-hidden" onClick={() => setShowHidden((s) => !s)}>
              {showHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} {showHidden ? "Hiding hidden" : "Show hidden"}
            </Button>
          )}
          <Button variant="outline" size="sm" data-testid="tasks-templates-button" onClick={openTemplates}><FileText className="h-4 w-4" /> Templates</Button>
          <Button variant="outline" size="sm" data-testid="tasks-ai-draft-button" onClick={() => setAiPrompt({ text: "" })} className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/40"><Sparkles className="h-4 w-4" /> AI draft</Button>
          <Button data-testid="tasks-new-button" className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setEditing({ column: columns[0]?.key, priority: "medium", assignee_ids: [], checklist: [] })}><Plus className="h-4 w-4" /> New task</Button>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="tasks-board">
          {columns.map((col) => {
            const list = byCol(col.key);
            return (
              <div key={col.key} data-testid={`task-column-${col.key}`}
                onDragOver={(e) => { e.preventDefault(); setDragCol(col.key); }}
                onDragLeave={() => setDragCol((p) => (p === col.key ? null : p))}
                onDrop={() => onDrop(col.key)}
                className={`flex w-72 shrink-0 flex-col rounded-xl border bg-muted/40 ${dragCol === col.key ? "border-primary ring-2 ring-primary/30" : "border-border"}`}>
                <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: col.tone }} />
                    <span className="text-sm font-bold text-foreground">{col.name}</span>
                  </div>
                  <span className="rounded-full bg-card px-2 py-0.5 text-xs font-semibold text-muted-foreground ring-1 ring-border">{list.length}</span>
                </div>
                <div className="flex flex-1 flex-col gap-2.5 p-2.5">
                  {list.map((c) => <Card key={c.task_id} c={c} />)}
                  <button data-testid={`task-add-${col.key}`} onClick={() => setEditing({ column: col.key, priority: "medium", assignee_ids: [], checklist: [] })}
                    className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-border py-2 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-primary">
                    <Plus className="h-3.5 w-3.5" /> Add card
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="tasks-list">
          {columns.map((col) => {
            const list = byCol(col.key);
            if (list.length === 0) return null;
            return (
              <div key={col.key}>
                <div className="flex items-center gap-2 bg-muted px-4 py-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: col.tone }} />
                  <span className="text-sm font-bold text-foreground">{col.name}</span>
                  <span className="text-xs text-muted-foreground">{list.length}</span>
                </div>
                <div className="divide-y divide-border">
                  {list.map((c) => {
                    const due = dueMeta(c.due_date);
                    const done = c.status === "completed";
                    return (
                      <div key={c.task_id} data-testid={`task-row-${c.task_id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50">
                        <span className={`flex-1 truncate text-sm font-medium text-foreground ${done ? "line-through opacity-60" : ""}`}>{c.title}{c.status === "hidden" && <span className="ml-2 text-xs text-muted-foreground">(hidden)</span>}</span>
                        {c.checklist_total > 0 && <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:inline-flex"><ListChecks className="h-3.5 w-3.5" />{c.checklist_done}/{c.checklist_total}</span>}
                        <PriorityChip p={c.priority} />
                        {due && <span className={`hidden items-center gap-1 text-xs sm:inline-flex ${due.tone}`}><CalendarDays className="h-3 w-3" />{due.label}</span>}
                        <AssigneeStack assignees={c.assignees} max={2} />
                        <CardMenu c={c} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {cards.length === 0 && <div className="px-4 py-12 text-center text-sm text-muted-foreground">No tasks yet — create one to get started.</div>}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.task_id ? "Edit task" : "New task"}</DialogTitle>
            <DialogDescription>Cards organise engineering work across priority columns.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label>
              <Input data-testid="task-title-input" value={editing?.title || ""} maxLength={140}
                onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} placeholder="Short, actionable title" /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Textarea data-testid="task-desc-input" rows={2} value={editing?.description || ""} maxLength={2000}
                onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))} placeholder="Details, links, steps…" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Column</Label>
                <Select value={editing?.column} onValueChange={(v) => setEditing((p) => ({ ...p, column: v }))}>
                  <SelectTrigger data-testid="task-column-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{columns.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Priority</Label>
                <Select value={editing?.priority} onValueChange={(v) => setEditing((p) => ({ ...p, priority: v }))}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(PRIORITY_META).map((k) => <SelectItem key={k} value={k}>{PRIORITY_META[k].label}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label>Due date</Label>
              <DuePicker value={editing?.due_date} onChange={(v) => setEditing((p) => ({ ...p, due_date: v }))} /></div>
            {/* Multiple assignees */}
            <div className="space-y-1.5">
              <Label>Assignees <span className="font-normal text-muted-foreground">(select one or more)</span></Label>
              <div className="flex flex-wrap gap-1.5" data-testid="task-assignees">
                {assignees.map((a) => {
                  const on = (editing?.assignee_ids || []).includes(a.user_id);
                  return (
                    <button key={a.user_id} type="button" data-testid={`task-assignee-${a.user_id}`} onClick={() => toggleAssignee(a.user_id)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs font-medium transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
                      <Avatar name={a.name} src={a.avatar_url} size="sm" single className="!h-5 !w-5 !text-[9px] !ring-1" />
                      {a.name.split(/\s+/)[0]}
                      {on && <Check className="h-3 w-3" />}
                    </button>
                  );
                })}
                {assignees.length === 0 && <span className="text-xs text-muted-foreground">No engineering members available.</span>}
              </div>
            </div>
            {/* Notes */}
            <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><StickyNote className="h-3.5 w-3.5" /> Notes</Label>
              <Textarea data-testid="task-notes-input" rows={2} value={editing?.notes || ""} maxLength={4000}
                onChange={(e) => setEditing((p) => ({ ...p, notes: e.target.value }))} placeholder="Running notes, context, updates…" /></div>
            {/* Checklist */}
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Checklist</Label>
              <div className="space-y-1.5" data-testid="task-checklist">
                {(editing?.checklist || []).map((it) => (
                  <div key={it.id} className="flex items-center gap-2">
                    <Checkbox checked={!!it.done} data-testid={`checklist-toggle-${it.id}`} onCheckedChange={(v) => updCheckItem(it.id, { done: !!v })} />
                    <Input value={it.text} data-testid={`checklist-text-${it.id}`} placeholder="Checklist item"
                      className={`h-8 ${it.done ? "line-through text-muted-foreground" : ""}`}
                      onChange={(e) => updCheckItem(it.id, { text: e.target.value })} />
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" data-testid={`checklist-del-${it.id}`} onClick={() => delCheckItem(it.id)}><X className="h-4 w-4 text-muted-foreground" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" data-testid="checklist-add" onClick={addCheckItem}><Plus className="h-4 w-4" /> Add item</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="task-save-button" className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={saveTask}>{editing?.task_id ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI draft dialog */}
      <Dialog open={!!aiPrompt} onOpenChange={(o) => !o && setAiPrompt(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" /> Draft an incident task with AI</DialogTitle>
            <DialogDescription>Describe what's happening in plain English. Claude will draft a task with a title, priority and checklist for you to review before creating.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="ai-draft-input" rows={4} autoFocus value={aiPrompt?.text || ""}
            onChange={(e) => setAiPrompt({ text: e.target.value })}
            placeholder="e.g. Primary SBC in Calgary is dropping 30% of SIP calls since 2pm; customers reporting one-way audio." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiPrompt(null)} disabled={aiLoading}>Cancel</Button>
            <Button data-testid="ai-draft-submit" onClick={runAiDraft} disabled={aiLoading} className="bg-violet-600 hover:bg-violet-700 text-white">
              {aiLoading ? "Drafting…" : <><Sparkles className="h-4 w-4" /> Draft task</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates dialog */}
      <Dialog open={tmplOpen} onOpenChange={setTmplOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Template cards</DialogTitle>
            <DialogDescription>Quick-start a card from a saved template, or save your own.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {templates.map((t) => (
              <div key={t.template_id} data-testid={`template-${t.template_id}`} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className="truncate text-sm font-semibold text-foreground">{t.title}</span><PriorityChip p={t.priority} />{t.is_seed && <span className="rounded bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">starter</span>}</div>
                  {t.description && <p className="truncate text-xs text-muted-foreground">{t.description}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button size="sm" variant="outline" data-testid={`template-use-${t.template_id}`} onClick={() => applyTemplate(t)}>Use</Button>
                  {(!t.is_seed || canManage) && <Button size="icon" variant="ghost" data-testid={`template-del-${t.template_id}`} onClick={() => delTemplate(t.template_id)}><X className="h-4 w-4 text-muted-foreground" /></Button>}
                </div>
              </div>
            ))}
            {templates.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No templates yet.</p>}
          </div>
          {newTmpl ? (
            <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3">
              <Input data-testid="new-template-title" placeholder="Template title" value={newTmpl.title} onChange={(e) => setNewTmpl((p) => ({ ...p, title: e.target.value }))} />
              <Textarea rows={2} placeholder="Description (optional)" value={newTmpl.description} onChange={(e) => setNewTmpl((p) => ({ ...p, description: e.target.value }))} />
              <div className="grid grid-cols-2 gap-2">
                <Select value={newTmpl.column || "any"} onValueChange={(v) => setNewTmpl((p) => ({ ...p, column: v === "any" ? null : v }))}>
                  <SelectTrigger><SelectValue placeholder="Any column" /></SelectTrigger>
                  <SelectContent><SelectItem value="any">Any column</SelectItem>{columns.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={newTmpl.priority} onValueChange={(v) => setNewTmpl((p) => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(PRIORITY_META).map((k) => <SelectItem key={k} value={k}>{PRIORITY_META[k].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2"><Button size="sm" variant="ghost" onClick={() => setNewTmpl(null)}>Cancel</Button><Button size="sm" data-testid="save-template-button" className="bg-primary text-primary-foreground" onClick={saveTemplate}>Save template</Button></div>
            </div>
          ) : (
            <Button variant="outline" size="sm" data-testid="add-template-button" onClick={() => setNewTmpl({ title: "", description: "", column: null, priority: "medium" })}><Plus className="h-4 w-4" /> New template</Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
